import { Injectable, OnDestroy } from '@angular/core';
import { FeatureFlag, isFeatureEnabled, requireFeatureEnabled } from '../feature-flags';
import { Subscription } from 'rxjs';
import type { UserIssuanceV3Response } from '@platform/api-client';
import { DriverSessionCryptographyService, StoredKeys } from './driver-session-cryptography.service';
import { DriverSessionIssuanceService } from './driver-session-issuance.service';
import { AppUserService } from './app-user.service';
import { EventBusService } from './event-bus.service';
import { AppEventType } from '../enums/app-events.enum';
import { RepositoryService } from '../baseServices/repository.service';
import { StoreName } from '../entities/StoreName';
import { LogService } from '../baseServices/logging/log.service';

/**
 * Merged credentials record persisted in the DriverServiceIssuance singleton table (RecordId=1).
 * Keys and issuance are written together so they can never drift out of sync.
 */
interface StoredCredentials extends StoredKeys {
  RecordId: number;
  issuance: UserIssuanceV3Response['issuance'];
  issuanceSignature: string;
  cachedAt: string;
}

export interface CredentialsValidationResult {
  valid: boolean;
  reason?: string;
  /** True when the issuance hasn't actually expired yet but is inside the refresh buffer window. */
  expiringSoon?: boolean;
}

/** Issuance payload needed to embed + sign a Driver Session token. */
export interface DriverSessionIssuance {
  issuance: UserIssuanceV3Response['issuance'];
  issuanceSignature: string;
}

/**
 * Thrown when no valid issuance can be established for signing a Driver Session
 * token. Callers (e.g. DriverSessionQrComponent) can catch this specifically to
 * distinguish an authentication problem from a generic generation failure.
 */
export class DriverSessionIssuanceUnavailableError extends Error {
  constructor(reason?: string) {
    super(reason ?? 'Driver session issuance is unavailable.');
    this.name = 'DriverSessionIssuanceUnavailableError';
  }
}

/**
 * Driver Session Credentials Service
 *
 * Owns the Driver Session issuance and signing-key lifecycle as a single unit
 * of work. Signs data with the stored ECDSA P-256 private key without ever
 * exposing the CryptoKey to callers.
 */

@Injectable({
  providedIn: 'root'
})
export class DriverSessionCredentialsService implements OnDestroy {
  private static readonly RECORD_ID = 1;
  private static readonly EXPIRATION_BUFFER_HOURS = 2;

  private readonly subscriptions: Subscription[] = [];
  /** In-memory copy of the stored record so repeated calls in the same session skip IndexedDB. */
  private memoryCache: StoredCredentials | null = null;
  /** Dedupes concurrent ensureIssuance() callers (e.g. sync + signing) into a single in-flight refresh. */
  private ensureIssuancePromise: Promise<void> | null = null;

  constructor(
    private driverSessionCryptographyService: DriverSessionCryptographyService,
    private driverSessionIssuanceService: DriverSessionIssuanceService,
    private appUserService: AppUserService,
    private eventBusService: EventBusService,
    private repository: RepositoryService,
    private logService: LogService
  ) {
    if (isFeatureEnabled(FeatureFlag.DriverSession)) {
      this.subscriptions.push(
        this.eventBusService.on(AppEventType.LOGOUT_SUCCESS, () => this.clearCredentials())
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /**
   * Remove stored keys and issuance so the next login always starts fresh
   * and a subsequent user can never reuse the previous user's credentials.
   */
  async clearCredentials(): Promise<void> {
    requireFeatureEnabled(FeatureFlag.DriverSession);
    this.memoryCache = null;
    await this.repository.delete(DriverSessionCredentialsService.RECORD_ID, StoreName.DriverServiceIssuance);
    this.logService.event('DriverSessionCredentials.Cleared', {});
  }

  /**
   * Sign arbitrary data with the stored ECDSA P-256 private key.
   * The CryptoKey never leaves this service.
   */
  async sign(data: Uint8Array): Promise<Uint8Array> {
    const record = await this.getRecord();
    if (!record?.publicKey || !record?.encryptedPrivateKey) {
      throw new DriverSessionIssuanceUnavailableError('No signing key found');
    }

    const privateKey = await this.driverSessionCryptographyService.getDecryptedPrivateKey(record);
    const signatureBuffer = await crypto.subtle.sign(
      { name: 'ECDSA', hash: 'SHA-256' },
      privateKey,
      new Uint8Array(data) // normalize to a concrete ArrayBuffer-backed view for BufferSource typing
    );
    return new Uint8Array(signatureBuffer);
  }

  /**
   * Rotate signing keys and refresh issuance, then persist both in a single
   * write so a partial failure can never leave stale keys paired with fresh
   * issuance (or vice versa). Call on login.
   */
  async rotateCredentials(): Promise<void> {
    requireFeatureEnabled(FeatureFlag.DriverSession);
    const keyMaterial = await this.driverSessionCryptographyService.generateKeyMaterial();
    await this.fetchAndPersistIssuance(keyMaterial);
  }

  /**
   * Validate that stored credentials belong to the current user, that the
   * signing key matches the key embedded in the issuance, and that the
   * issuance is not expired (or expiring within the refresh buffer).
   */
  async validateCredentials(): Promise<CredentialsValidationResult> {
    return this.validate(await this.getRecord());
  }

  /**
   * Validate stored credentials and refresh them if invalid/expiring.
   * Call from the regular sync process (not just login) so a failed
   * login-time rotation always has a later chance to recover.
   *
   * Concurrent callers (sync + signing) share a single in-flight refresh.
   * If the existing issuance is only inside the refresh buffer (not yet
   * actually expired) and the refresh attempt fails, the stale-but-valid
   * issuance is kept and reused rather than failing the caller.
   */
  async ensureIssuance(): Promise<void> {
    requireFeatureEnabled(FeatureFlag.DriverSession);
    if (!this.ensureIssuancePromise) {
      this.ensureIssuancePromise = this.doEnsureIssuance().finally(() => {
        this.ensureIssuancePromise = null;
      });
    }

    return this.ensureIssuancePromise;
  }

  /**
   * Ensure credentials are valid/fresh and return the issuance payload needed
   * to sign a Driver Session token. Callers should never fetch issuance
   * themselves -- this is the single entry point for that concern.
   */
  async getIssuanceForToken(): Promise<DriverSessionIssuance> {
    requireFeatureEnabled(FeatureFlag.DriverSession);
    try {
      await this.ensureIssuance();
    } catch (error) {
      this.logService.error('DriverSessionCredentials.EnsureIssuanceFailed', error as any);
      throw new DriverSessionIssuanceUnavailableError();
    }

    const record = await this.getRecord();

    if (!record?.issuance || !record?.issuanceSignature) {
      throw new DriverSessionIssuanceUnavailableError();
    }

    return { issuance: record.issuance, issuanceSignature: record.issuanceSignature };
  }

  private async doEnsureIssuance(): Promise<void> {
    const record = await this.getRecord();
    const validation = this.validate(record);
    if (validation.valid) {
      return;
    }

    this.logService.event('DriverSessionCredentials.EnsureIssuanceRefreshing', { reason: validation.reason });

    try {
      if (record?.publicKey && record?.encryptedPrivateKey) {
        await this.refreshIssuanceReusingKey(record);
      } else {
        await this.rotateCredentials();
      }
    } catch (error) {
      if (validation.expiringSoon) {
        this.logService.event('DriverSessionCredentials.RefreshFailedUsingExisting', { reason: validation.reason });
        return;
      }
      throw error;
    }
  }

  private validate(record: StoredCredentials | null): CredentialsValidationResult {
    if (!record?.publicKey || !record?.issuance || !record?.issuanceSignature) {
      return { valid: false, reason: 'No credentials found' };
    }

    const expectedUserId = this.appUserService.currentUserId;
    if (expectedUserId != null && record.issuance.userId !== expectedUserId) {
      return { valid: false, reason: 'Issuance does not belong to the current user' };
    }

    if (record.issuance.publicKey !== record.publicKey) {
      return { valid: false, reason: 'Issuance public key does not match the stored signing key' };
    }

    const expiration = new Date(record.issuance.expiration);
    if (isNaN(expiration.getTime())) {
      return { valid: false, reason: 'Issuance expiration is malformed' };
    }

    const now = new Date();
    if (expiration <= now) {
      return { valid: false, reason: 'Issuance has expired' };
    }

    const bufferTime = new Date(now);
    bufferTime.setHours(bufferTime.getHours() + DriverSessionCredentialsService.EXPIRATION_BUFFER_HOURS);
    if (expiration <= bufferTime) {
      return { valid: false, reason: 'Issuance is expiring soon', expiringSoon: true };
    }

    return { valid: true };
  }

  /**
   * Reads the merged credentials record, preferring the in-memory copy over IndexedDB.
   */
  private async getRecord(): Promise<StoredCredentials | null> {
    requireFeatureEnabled(FeatureFlag.DriverSession);
    if (this.memoryCache) {
      return this.memoryCache;
    }

    const record = await this.repository.getByLocalId<StoredCredentials>(
      StoreName.DriverServiceIssuance,
      DriverSessionCredentialsService.RECORD_ID
    );
    this.memoryCache = record ?? null;
    return this.memoryCache;
  }

  /**
   * Refresh issuance only, reusing the currently stored signing key material
   * (no key rotation) so a mid-session refresh can't invalidate an
   * already-scanned QR whose signature is still tied to the old key.
   */
  private async refreshIssuanceReusingKey(current: StoredCredentials): Promise<void> {
    await this.fetchAndPersistIssuance({
      publicKey: current.publicKey,
      encryptedPrivateKey: current.encryptedPrivateKey,
      keysCreatedAt: current.keysCreatedAt
    });
  }

  /**
   * Fetch issuance for the given key material, verify it belongs to the
   * current user before trusting it, then persist both together.
   */
  private async fetchAndPersistIssuance(keyMaterial: StoredKeys): Promise<void> {
    const issuanceResponse = await this.driverSessionIssuanceService.fetchIssuance(keyMaterial.publicKey);

    const expectedUserId = this.appUserService.currentUserId;
    if (expectedUserId != null && issuanceResponse.issuance.userId !== expectedUserId) {
      throw new Error('Issuance response does not belong to the current user');
    }

    await this.persistCredentials(keyMaterial, issuanceResponse);

    this.logService.event('DriverSessionCredentials.Rotated', {
      expiresAt: issuanceResponse.issuance.expiration,
      userId: issuanceResponse.issuance.userId
    });
  }

  /**
   * Persist newly generated key material and issuance together as one write,
   * replacing any previous credentials record atomically.
   */
  private async persistCredentials(
    keyMaterial: StoredKeys,
    issuanceResponse: UserIssuanceV3Response
  ): Promise<void> {
    const record: StoredCredentials = {
      RecordId: DriverSessionCredentialsService.RECORD_ID,
      publicKey: keyMaterial.publicKey,
      encryptedPrivateKey: keyMaterial.encryptedPrivateKey,
      keysCreatedAt: keyMaterial.keysCreatedAt,
      issuance: issuanceResponse.issuance,
      issuanceSignature: issuanceResponse.issuanceSignature,
      cachedAt: new Date().toISOString()
    };

    await this.repository.update(record, StoreName.DriverServiceIssuance);
    this.memoryCache = record;
  }
}
